// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * WaitingState - Displays a waiting message for non-active players
 *
 * Used when a player is waiting for another player to take action,
 * such as waiting for the judge to pick a winner or waiting for
 * other players to submit their cards.
 */

import type { ReactNode } from 'react';

type WaitingVariant = 'default' | 'judge' | 'players' | 'host';

interface WaitingStateProps {
  /** The name or role being waited on */
  waitingFor: string;
  /** Optional custom message (overrides default) */
  message?: string;
  /** Visual variant */
  variant?: WaitingVariant;
  /** Show animated dots */
  showAnimation?: boolean;
  /** Optional content to show while waiting (e.g., player's own cards) */
  children?: ReactNode;
  /** Custom class names */
  className?: string;
}

const variantStyles: Record<WaitingVariant, { icon: string; color: string }> = {
  default: {
    icon: '⏳',
    color: 'text-gray-600 dark:text-gray-400',
  },
  judge: {
    icon: '⚖️',
    color: 'text-purple-600 dark:text-purple-400',
  },
  players: {
    icon: '👥',
    color: 'text-blue-600 dark:text-blue-400',
  },
  host: {
    icon: '👑',
    color: 'text-amber-600 dark:text-amber-400',
  },
};

export function WaitingState({
  waitingFor,
  message,
  variant = 'default',
  showAnimation = true,
  children,
  className = '',
}: WaitingStateProps) {
  const { icon, color } = variantStyles[variant];

  const defaultMessage = `Waiting for ${waitingFor}`;
  const displayMessage = message || defaultMessage;

  return (
    <div
      className={`flex flex-col items-center justify-center py-8 px-4 ${className}`}
      role="status"
      aria-live="polite"
    >
      {/* Animated icon */}
      <div className="text-4xl mb-4" aria-hidden="true">
        {showAnimation ? <span className="inline-block animate-bounce">{icon}</span> : icon}
      </div>

      {/* Message */}
      <p className={`text-lg font-medium ${color}`}>
        {displayMessage}
        {showAnimation && <AnimatedDots />}
      </p>

      {/* Hint text */}
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{getHintText(variant)}</p>

      {/* Optional children (e.g., show player's hand while waiting) */}
      {children && (
        <div className="mt-6 w-full">
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex ml-1" aria-hidden="true">
      <span className="animate-pulse" style={{ animationDelay: '0ms' }}>
        .
      </span>
      <span className="animate-pulse" style={{ animationDelay: '200ms' }}>
        .
      </span>
      <span className="animate-pulse" style={{ animationDelay: '400ms' }}>
        .
      </span>
    </span>
  );
}

function getHintText(variant: WaitingVariant): string {
  switch (variant) {
    case 'judge':
      return 'The judge is reviewing submissions';
    case 'players':
      return 'Other players are making their choices';
    case 'host':
      return 'The host will start the game soon';
    default:
      return 'Hang tight, the action will continue shortly';
  }
}

export default WaitingState;
